// src/pages/DashboardClient.js

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../context/AuthContext";
import { normalizeRole, ROLE_VALUE, getRoleLabel } from "../constants/roleLabels";
import { formatDuration } from "../utils/duration";
import { waitingOnViewerCount } from "../utils/queueMetrics";
import { getTopHelpersForProblem } from "../services/matchingService";
import { useIncidentQueue } from "../hooks/useIncidentQueue";

import OperationsShell from "../components/dashboard/shell/OperationsShell";
import OperationsSummary from "../components/dashboard/OperationsSummary";
import DashboardCard from "../components/dashboard/DashboardCard";
import ExpertMatchCard from "../components/dashboard/ExpertMatchCard";
import ConversationDrawer from "../components/dashboard/ConversationDrawer";
import ConfirmDialog from "../components/ConfirmDialog";
import IncidentReportView from "../components/workspace/IncidentReportView";

/**
 * The customer's operations console.
 *
 * A customer's questions are narrower than an expert's: is someone on this, have
 * they said anything yet, and is it me holding it up. The strip answers all
 * three, which the previous gradient "This Summary" panel did not — it counted
 * incidents and showed an average rating that was hardcoded to zero.
 */

function SectionHeading({ title, count, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 className="text-base font-semibold text-slate-900">
        {title}
        {typeof count === "number" && (
          <span className="ml-2 text-sm font-normal tabular-nums text-slate-500">
            {count}
          </span>
        )}
      </h2>
      {children}
    </div>
  );
}

function DashboardClient() {
  const { currentUser, currentUserRole, logout, ready } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("overview");
  const [matchesByProblem, setMatchesByProblem] = useState({});
  const [showConversations, setShowConversations] = useState(false);
  const [reportViewId, setReportViewId] = useState(null);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);

  const {
    active,
    completed,
    summary,
    confirmResolution,
    requestRefund,
    dismissIncident,
    cancelIncident,
  } = useIncidentQueue({ currentUser, viewerRole: "client" });

  useEffect(() => {
    if (!ready) return;
    if (!currentUser || normalizeRole(currentUserRole) !== ROLE_VALUE.requester) {
      navigate("/login");
    }
  }, [currentUser, currentUserRole, ready, navigate]);

  // Suggestions only mean anything while an incident is unclaimed. Ranking one
  // that already has an expert on it is work nobody asked for.
  const unclaimedKey = active
    .filter((problem) => problem.status === "open")
    .map((problem) => problem.id)
    .join("|");

  useEffect(() => {
    if (!unclaimedKey) return undefined;

    let cancelled = false;
    const ids = unclaimedKey.split("|");

    Promise.all(
      ids.map(async (problemId) => {
        try {
          const { matches } = await getTopHelpersForProblem(problemId, 3);
          return [problemId, matches];
        } catch (error) {
          console.error("Could not rank experts:", error);
          return [problemId, []];
        }
      })
    ).then((entries) => {
      if (!cancelled) setMatchesByProblem(Object.fromEntries(entries));
    });

    return () => {
      cancelled = true;
    };
  }, [unclaimedKey]);

  const handleMarkResolved = useCallback(
    (problemId) => confirmResolution(problemId),
    [confirmResolution]
  );

  const confirmWithdrawal = async () => {
    if (!pendingWithdrawal) return;
    setWithdrawing(true);
    await cancelIncident(pendingWithdrawal.id);
    setWithdrawing(false);
    setPendingWithdrawal(null);
  };

  const metrics = [
    { id: "active", label: "Open incidents", value: summary.activeCount },
    {
      id: "unclaimed",
      label: "Awaiting an expert",
      value: summary.unclaimedCount || null,
      tone: "attention",
    },
    {
      id: "unanswered",
      label: "No reply yet",
      value: summary.awaitingFirstResponseCount || null,
      tone: "attention",
    },
    {
      id: "waiting",
      label: "Waiting on you",
      value: waitingOnViewerCount(summary, "client") || null,
      tone: "attention",
      hint: "Your expert is blocked until you respond",
    },
    {
      id: "oldest",
      label: "Longest open",
      value:
        summary.oldestActiveMs != null
          ? formatDuration(summary.oldestActiveMs)
          : null,
    },
    {
      id: "resolved",
      label: "Resolved",
      value: summary.resolvedCount || null,
      tone: "positive",
    },
  ];

  const tabs = [
    { id: "overview", label: "Open incidents", badge: summary.activeCount },
    { id: "closed", label: "Closed", badge: completed.length },
  ];

  const reportIncidentButton = (
    <button
      type="button"
      onClick={() => navigate("/submit-problem")}
      className="rounded-lg bg-blue-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
    >
      Report an incident
    </button>
  );

  return (
    <OperationsShell
      user={currentUser}
      roleLabel={getRoleLabel("client")}
      historyPath="/clienthistory"
      onLogout={async () => {
        await logout();
        navigate("/");
      }}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <OperationsSummary
        title="Your incidents"
        subtitle={
          summary.activeCount === 0
            ? "Nothing open. Report an incident and we will route it to a payments specialist."
            : undefined
        }
        metrics={metrics}
        action={
          <div className="flex items-center gap-2">
            <ConversationDrawer
              problems={active}
              currentUserId={currentUser?.uid}
              open={showConversations}
              setOpen={setShowConversations}
            />
            {reportIncidentButton}
          </div>
        }
      />

      {activeTab === "overview" && (
        <div className="space-y-6">
          {active.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center">
              <p className="text-sm font-semibold text-slate-900">
                No open incidents
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                Report a Stripe or payments incident and we will route it to a
                specialist in your lane.
              </p>
              <div className="mt-4 flex justify-center">
                {reportIncidentButton}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {active.map((problem) => {
                const matches = matchesByProblem[problem.id] || [];

                return (
                  <section key={problem.id} className="space-y-3">
                    <DashboardCard
                      problem={problem}
                      userRole="client"
                      currentUserId={currentUser?.uid}
                      onMarkComplete={handleMarkResolved}
                      onCancel={() => setPendingWithdrawal(problem)}
                      onRequestRefund={requestRefund}
                    />

                    {problem.status === "open" && matches.length > 0 && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="mb-3">
                          <h3 className="text-sm font-semibold text-slate-900">
                            Suggested specialists
                          </h3>
                          <p className="mt-0.5 text-xs text-slate-500">
                            Ranked by coverage of your incident's tags, track
                            record, overlapping working hours, and current load.
                            You can also wait for the queue.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          {matches.map((match) => (
                            <ExpertMatchCard
                              key={
                                match.helper.uid ||
                                match.helper.id ||
                                match.helper.email
                              }
                              helper={match.helper}
                              isStrongMatch={match.isStrongMatch}
                              reasons={match.reasons}
                              problemId={problem.id}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === "closed" && (
        <div className="space-y-3">
          <SectionHeading title="Closed incidents" count={completed.length} />
          {completed.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
              <p className="text-sm font-semibold text-slate-900">
                Nothing closed yet
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Resolved incidents and their reports stay here for your records.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {completed.map((problem) => (
                <div key={problem.id} className="space-y-2">
                  <DashboardCard
                    problem={problem}
                    userRole="client"
                    currentUserId={currentUser?.uid}
                    onRequestRefund={requestRefund}
                  />
                  <div className="flex items-center gap-2">
                    {problem.incidentReportId && (
                      <button
                        type="button"
                        onClick={() => setReportViewId(problem.incidentReportId)}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50"
                      >
                        View resolution report
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => dismissIncident(problem.id)}
                      className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
                    >
                      Hide from dashboard
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(pendingWithdrawal)}
        title="Withdraw this incident?"
        body={
          pendingWithdrawal
            ? `"${pendingWithdrawal.title}" will be removed from the expert queue and cannot be restored. Any payment is handled by operations separately.`
            : undefined
        }
        confirmLabel="Withdraw incident"
        busy={withdrawing}
        onConfirm={confirmWithdrawal}
        onCancel={() => setPendingWithdrawal(null)}
      />

      {reportViewId && (
        <div
          className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4"
          onClick={() => setReportViewId(null)}
        >
          <div
            className="my-8 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <p className="text-sm font-semibold text-slate-900">
                Resolution report
              </p>
              <button
                type="button"
                onClick={() => setReportViewId(null)}
                className="rounded-lg px-2 py-1 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
              >
                Close
              </button>
            </div>
            <div className="p-5">
              <IncidentReportView reportId={reportViewId} />
            </div>
          </div>
        </div>
      )}
    </OperationsShell>
  );
}

export default DashboardClient;
